import type {
  QuickBooksConnection,
  QuickBooksProjectLink,
} from 'store/api/quickbooks';

export const getLocationHeader = (isHideForSalesAndManager?: boolean) => {
  const titles = ['#', 'Size', 'Type', 'Event Type'];

  if (!isHideForSalesAndManager) {
    titles.splice(1, 0, '$');
  }

  return titles;
};

export const QUICKBOOKS_LINK_STATUS = {
  checking: 'Checking…',
  unavailable: 'Unavailable',
  notLinked: 'Not linked yet',
  blocked: 'Blocked — needs review in Control Center',
  linked: (qboProjectId: string) => `Linked · QBO #${qboProjectId}`,
} as const;

export interface QuickBooksLinkStatusInput {
  eventName?: string;
  eventId: number | string;
  isProjectsError?: boolean;
  isProjectsLoading?: boolean;
  isConnectionError?: boolean;
  isConnectionLoading?: boolean;
  connection?: Pick<QuickBooksConnection, 'status' | 'realmId'> | null;
  links?: Pick<
    QuickBooksProjectLink,
    'state' | 'eventId' | 'qboProjectId' | 'qboProjectName'
  >[];
}

export interface QuickBooksLinkStatus {
  status: string;
  projectName: string;
}

/**
 * Derives the event page's "QuickBooks Project" and "QuickBooks Status"
 * values from the connection and the company's QbProjectLinks.
 */
export const getQuickBooksLinkStatus = ({
  links,
  eventId,
  eventName,
  connection,
  isProjectsError,
  isConnectionError,
  isProjectsLoading,
  isConnectionLoading,
}: QuickBooksLinkStatusInput): QuickBooksLinkStatus => {
  const expectedName = `CLIX-${eventId} - ${eventName ?? ''}`;
  const withStatus = (status: string) => ({
    status,
    projectName: expectedName,
  });

  if (isConnectionLoading) {
    return withStatus(QUICKBOOKS_LINK_STATUS.checking);
  }

  if (
    isConnectionError ||
    connection?.status !== 'active' ||
    !connection.realmId ||
    isProjectsError
  ) {
    return withStatus(QUICKBOOKS_LINK_STATUS.unavailable);
  }

  if (isProjectsLoading || !links) {
    return withStatus(QUICKBOOKS_LINK_STATUS.checking);
  }

  const link = links.find((item) => Number(item.eventId) === Number(eventId));

  if (!link) {
    return withStatus(QUICKBOOKS_LINK_STATUS.notLinked);
  }

  return {
    projectName: link.qboProjectName || expectedName,
    status:
      link.state === 'verified'
        ? QUICKBOOKS_LINK_STATUS.linked(link.qboProjectId)
        : QUICKBOOKS_LINK_STATUS.blocked,
  };
};
